import {
  showExternalCollabModal,
  showExternalPermissionModal,
} from "actions/modal";
import { COLLABORATION_VIEW_LINKS } from "constants/Events";
import { User } from "features/user";
import { trackEventWithUser, useTrackEvent } from "libs/analytics";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUser } from "selectors/commonSelectors";
import { Store } from "store";
import { MODAL_NAMES, MODAL_SEARCH_PARAM_NAME } from "constants/Modals";
import {
  hasExternalApprovals,
  hasApprovals,
  hasTeamwork,
} from "utils/feature-gates";

// TODO add argument types
export const showExternalCollabModalOrPermissionModal = ({
  user,
  dispatch,
  trackEvent,
  location,
  navigate,
}: any) => {
  // Show the external review modal directly if the user has the External
  // Approvals add-on (or is on Accelerate). Users with approvals/teamwork but
  // no external entitlement see the upsell-style permission modal so they can
  // learn about the add-on.
  if (hasExternalApprovals(user)) {
    dispatch(showExternalCollabModal());
    trackEvent({
      eventName: COLLABORATION_VIEW_LINKS,
      metadata: { location },
    });
  } else if (hasApprovals(user) || hasTeamwork(user)) {
    dispatch(showExternalPermissionModal());
  } else {
    dispatch(showExternalPermissionModal());
  }

  navigate({
    search: `?${MODAL_SEARCH_PARAM_NAME}=${MODAL_NAMES.EXTERNAL_REVIEW}`,
  });
};

export const showExternalModal = ({
  openFrom,
  navigate,
}: {
  openFrom: string;
  navigate: any;
}) => {
  const dispatch = Store.dispatch;
  const user = getUser(Store.getState());
  const trackEvent = ({
    eventName,
    metadata,
  }: {
    eventName: any;
    metadata: any;
  }) => {
    trackEventWithUser(eventName, metadata, user);
  };

  showExternalCollabModalOrPermissionModal({
    user,
    dispatch,
    trackEvent,
    location: openFrom,
    navigate,
  });
};

export default function useShowExternalModal(openFrom: string): () => void {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const user: User = useSelector(getUser);
  const navigate = useNavigate();

  // userRef is updated every render so the callback always reads the latest
  // user without needing `user` in deps. dispatch / trackEvent / navigate are
  // stable (react-redux / analytics / react-router v6).
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  });

  const dispatchModal = useCallback(() => {
    showExternalCollabModalOrPermissionModal({
      user: userRef.current,
      dispatch,
      trackEvent,
      location: openFrom,
      navigate,
    });
  }, [dispatch, navigate, openFrom, trackEvent]);

  return dispatchModal;
}
